import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { proxy } from 'valtio';

import { cx } from '@libs/classnames';
import { AnimatedNode } from '@ui/components/Animation';
import { FancyBadge } from '@ui/components/FancyBadge';
import { Loader } from '@ui/components/Loader';
import { Tab, Tabs } from '@ui/components/Tabs';
import { IconSettings } from '@ui/icons-buttons/IconSettings';
import { CameraIcon } from '@ui/icons/CameraIcon';
import { ErrorIcon } from '@ui/icons/ErrorIcon';

import { MediaDevices } from '@features/video/hooks/useMediaDevices';
import {
  Highlighted,
  RefereeConnection,
  RefereeInfo,
  RefereePlayer,
  RefereeRoomInfo,
  RoomRef,
  Speaking,
} from '@features/video/types';
import { playerTiles, RefereeTile } from '@features/video/ui/PlayerTile';
import { ReconnectingBanner } from '@features/video/ui/ReconnectingBanner';
import { RefereeJoin } from '@features/video/ui/RefereeJoin';

import styles from './RefereeRoomPage.module.css';

// Что показываем: всех игроков комнаты, играющих, без партии, удаленных или судей
type View = 'all' | 'playing' | 'idle' | 'removed' | 'referees';

// Пустая вкладка: один текст на все
const EMPTY_TEXT = 'Нет игроков';

// Игрок вышел из игры: во вкладке «Играют» плитка еще столько стоит на месте, чтобы не
// пропадать из-под курсора
const SETTLE_MS = 5000;

// Ушедшая плитка доигрывает анимацию ухода: ее ключ держим еще столько
const GHOST_MS = 1000;

export type Props = {
  className?: string;
  // комнаты мультикомнаты: одна — «Комната N», больше — «Мультикомната». Заголовок —
  // кнопка выбора комнат; пока не все комнаты подключились — последняя плитка сетки
  // с загрузчиком и ошибками комнат
  rooms: RefereeRoomInfo[];
  onPickRooms?: () => void;
  // модалка выбора комнат открыта: шестеренка в заголовке держится и без наведения
  isPickerOpen?: boolean;
  onRetryRoom?: (room: RoomRef) => void;
  connection: RefereeConnection;
  // ошибка входа или потери связи
  error?: string | null;
  referees: RefereeInfo[];
  players: RefereePlayer[];
  // горячее: identity говорящих устройств и судей; плитки подписываются сами
  speakingProxy?: Speaking;
  micOn: boolean;
  // микрофон при входе: тумблер на экране входа
  onToggleMic: () => void;
  devices: MediaDevices;
  onJoin: () => void;
  onRetry?: () => void;
  onMute?: (userId: string, deviceId: string) => void;
  // комната записи игрока: в мультикомнате один человек может стоять в двух
  onKick?: (userId: string, room?: RoomRef) => void;
  onRestore?: (userId: string, room?: RoomRef) => void;
  onInvitePrivate?: (userId: string, room?: RoomRef) => void;
  onReturnToRoom?: (userId: string, room?: RoomRef) => void;
  // адрес страницы приватной комнаты игрока
  privateRoomHref?: (userId: string) => string;
  onOpenGame?: (gameId: string, tournamentId: string) => void;
  // закрепленные игроки для подписки: им видео по размеру плитки, сетке — младший слой
  onPinnedChange?: (userIds: string[]) => void;
  // игроки с плитками на странице: только им подписка на видео
  onShownChange?: (userIds: string[]) => void;
};

// Страница судьи: игроки одной видеокомнаты, удаленные из нее и другие судьи
export function RefereeRoomPage({
  className,
  rooms,
  onPickRooms,
  isPickerOpen,
  onRetryRoom,
  connection,
  error,
  referees,
  players,
  speakingProxy,
  micOn,
  onToggleMic,
  devices,
  onJoin,
  onRetry,
  onMute,
  onKick,
  onRestore,
  onInvitePrivate,
  onReturnToRoom,
  privateRoomHref,
  onOpenGame,
  onPinnedChange,
  onShownChange,
}: Props) {
  const isConnected = connection === 'live' || connection === 'reconnecting';
  const [view, setView] = useState<View>('all');
  const title = rooms.length > 1 ? 'Мультикомната' : `Комната ${rooms[0]?.roomNumber ?? ''}`;
  const isJoiningRooms =
    isConnected && rooms.some((r) => r.connection === 'connecting' || r.connection === 'error');

  // Курсор над плиткой подсвечивает все плитки игрока — горячее значение, перерисовываются
  // только плитки этого игрока; закрепленные — крупно сверху в порядке закрепления (новый
  // закреп встает в конец) и при любом фильтре
  const [hoverProxy] = useState(() => proxy<Highlighted>({}));
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const togglePin = (id: string) =>
    setPinnedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Только по алфавиту: порядок не меняется от статуса, людей легко искать, а по статусу
  // есть вкладки. Фильтры смотрят на отстающий флаг: подпись «Вне игры» появляется
  // сразу, а из «Играют» плитка пропадает через паузу
  const isPlaying = useSettledPlaying(players, view);
  const sorted = useMemo(
    () => [...players].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [players],
  );
  const lists: Record<Exclude<View, 'referees'>, RefereePlayer[]> = {
    all: sorted.filter((p) => !p.removed),
    playing: sorted.filter((p) => !p.removed && isPlaying(p)),
    idle: sorted.filter((p) => !p.removed && !isPlaying(p)),
    removed: sorted.filter((p) => p.removed),
  };
  const shown = view === 'referees' ? [] : lists[view];
  const pinned = pinnedIds.flatMap((key) => players.filter((p) => p.key === key));
  const rest = shown.filter((p) => !pinnedIds.includes(p.key));

  // Клиентам — userId закрепленных: потолок слоя снимается участнику во всех его комнатах
  const pinnedUsers = [...new Set(pinned.map((p) => p.userId))].join(',');
  useEffect(() => {
    onPinnedChange?.(pinnedUsers ? pinnedUsers.split(',') : []);
  }, [pinnedUsers, onPinnedChange]);

  // Видео подписано только тем, чьи плитки на странице; ключ строкой, чтобы не дергать
  // подписки на каждый рендер
  const shownKey = [...new Set([...pinned, ...rest].map((p) => p.userId))].join(',');
  useEffect(() => {
    onShownChange?.(shownKey ? shownKey.split(',') : []);
  }, [shownKey, onShownChange]);

  const tabs: Tab<View>[] = [
    tab('all', 'Все'),
    tab('playing', 'Играют', lists.playing.length),
    tab('idle', 'Без игр', lists.idle.length),
    tab('removed', 'Удалены', lists.removed.length),
    tab('referees', 'Судьи', referees.length),
  ];

  const tiles = (p: RefereePlayer) => {
    // партия, удаление и приват — в своем турнире и своей комнате: у игрока мультикомнаты
    // они свои
    const tournamentId = p.tournamentId;
    const room =
      tournamentId !== undefined && p.roomNumber !== undefined
        ? { tournamentId, roomNumber: p.roomNumber }
        : undefined;
    return playerTiles({
      player: p,
      speakingProxy,
      hoverProxy,
      pinned: pinnedIds.includes(p.key),
      onPin: p.removed ? undefined : () => togglePin(p.key),
      onMute: onMute && ((deviceId) => onMute(p.userId, deviceId)),
      onKick: onKick && (() => onKick(p.userId, room)),
      onRestore: onRestore && (() => onRestore(p.userId, room)),
      onInvitePrivate: onInvitePrivate && (() => onInvitePrivate(p.userId, room)),
      onReturnToRoom: onReturnToRoom && (() => onReturnToRoom(p.userId, room)),
      privateRoomHref: privateRoomHref?.(p.userId),
      onOpenGame:
        onOpenGame && tournamentId !== undefined
          ? (gameId) => onOpenGame(gameId, tournamentId)
          : undefined,
    });
  };

  return (
    <div className={cx(styles.page, className)}>
      <header className={styles.page__header}>
        <h1 className={styles.page__title}>
          {/* заголовок — кнопка выбора комнат: шрифт и цвет те же, на наведении и при
              открытой модалке камера становится шестеренкой */}
          <button
            type="button"
            className={cx(styles.page__pick, { [styles.is_open]: isPickerOpen })}
            title="Присоединить другие комнаты"
            onClick={onPickRooms}
          >
            <CameraIcon className={cx(styles.page__icon, styles.page__camera)} />
            <IconSettings className={cx(styles.page__icon, styles.page__gear)} />· {title}
          </button>
        </h1>
        {isConnected && (
          <Tabs className={styles.page__tabs} flat tabs={tabs} value={view} onChange={setView} />
        )}
        {/* контролы стрима судьи живут в липкой шапке сайта: их ставит виджет */}
      </header>

      {(connection === 'setup' || connection === 'connecting') && (
        <RefereeJoin
          title={title}
          devices={devices}
          error={error}
          isJoining={connection === 'connecting'}
          micOn={micOn}
          onToggleMic={onToggleMic}
          onJoin={onJoin}
        />
      )}

      {connection === 'reconnecting' && <ReconnectingBanner />}

      {connection === 'error' && (
        <Notice
          icon={
            <span className={styles.notice__error}>
              <ErrorIcon />
            </span>
          }
          title="Нет связи с комнатой"
          text={error ?? undefined}
        >
          {onRetry && <FancyBadge onClick={onRetry}>Подключиться снова</FancyBadge>}
        </Notice>
      )}

      {isConnected && view !== 'referees' && (
        <div className={styles.page__body}>
          {pinned.length > 0 && (
            <div className={styles.page__pinned}>
              <AnimatedTiles>{pinned.flatMap(tiles)}</AnimatedTiles>
            </div>
          )}
          <Grid empty={shown.length === 0 ? EMPTY_TEXT : undefined}>
            {/* ключ по вкладке: смена вкладки — первый рендер списка, без анимаций */}
            <AnimatedTiles key={view}>{rest.flatMap(tiles)}</AnimatedTiles>
            {isJoiningRooms && <RoomsStatus rooms={rooms} onRetry={onRetryRoom} />}
          </Grid>
        </div>
      )}

      {isConnected && view === 'referees' && (
        <Grid>
          {referees.map((r) => (
            // свой микрофон страница знает точнее, чем список
            <RefereeTile
              key={r.id}
              referee={r.isMe ? { ...r, micOn } : r}
              speakingProxy={speakingProxy}
              privateRoomHref={r.privateWith && privateRoomHref?.(r.privateWith.userId)}
            />
          ))}
        </Grid>
      )}
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Плитки с анимацией появления и ухода. Своя история вместо AnimatedDynamicNodes:
// та теряет уходящую плитку при любой перерисовке страницы, а страница перерисовывается
// часто (наведение, индикатор голоса). Ушедшая плитка остается на своем месте, пока
// доигрывает анимацию; на первом рендере ничего не анимируем (смена вкладки — тоже первый
// рендер: список под ключом вкладки)
function AnimatedTiles({ children }: { children: ReactElement[] }) {
  const prev = useRef<string[] | null>(null);
  const ghosts = useRef(new Map<string, { index: number; left: number }>());
  const now = Date.now();
  const keys = children.map((el) => String(el.key));
  const present = new Set(keys);

  prev.current?.forEach((key, index) => {
    if (!present.has(key) && !ghosts.current.has(key))
      ghosts.current.set(key, { index, left: now });
  });
  ghosts.current.forEach((g, key) => {
    if (present.has(key) || now - g.left > GHOST_MS) ghosts.current.delete(key);
  });

  const items: { key: string; el: ReactElement | null }[] = children.map((el, i) => ({
    key: keys[i]!,
    el,
  }));
  [...ghosts.current.entries()]
    .sort((a, b) => a[1].index - b[1].index)
    .forEach(([key, g]) => items.splice(Math.min(g.index, items.length), 0, { key, el: null }));

  const before = prev.current;
  prev.current = keys;
  return (
    <>
      {items.map(({ key, el }) => (
        <AnimatedNode
          key={key}
          animation="scale-fade"
          initial={before !== null && !before.includes(key)}
        >
          {el}
        </AnimatedNode>
      ))}
    </>
  );
}

// Флаг «играет» с задержкой на выход из игры: игрок, чья партия закончилась, еще
// SETTLE_MS считается играющим, чтобы плитки не пропадали под рукой судьи. Вход в игру —
// сразу. Смена вкладки сбрасывает задержку: судья сам попросил свежий список
function useSettledPlaying(players: RefereePlayer[], view: View): (p: RefereePlayer) => boolean {
  // вне игры, но пока показаны как играющие
  const [held, setHeld] = useState<Set<string>>(() => new Set());
  const prev = useRef(new Map<string, boolean>());
  const timers = useRef(new Map<string, number>());

  const release = (key: string) => {
    window.clearTimeout(timers.current.get(key));
    timers.current.delete(key);
    setHeld((s) => {
      if (!s.has(key)) return s;
      const next = new Set(s);
      next.delete(key);
      return next;
    });
  };

  useEffect(() => {
    players.forEach((p) => {
      const was = prev.current.get(p.key);
      prev.current.set(p.key, p.playing);
      if (p.playing) {
        if (timers.current.has(p.key)) release(p.key);
      } else if (was && !timers.current.has(p.key)) {
        setHeld((s) => new Set(s).add(p.key));
        timers.current.set(
          p.key,
          window.setTimeout(() => release(p.key), SETTLE_MS),
        );
      }
    });
  }, [players]);

  // Смена вкладки и уход со страницы: задержки снимаем
  useEffect(() => {
    const all = timers.current;
    return () => {
      all.forEach((t) => window.clearTimeout(t));
      all.clear();
      setHeld(new Set());
    };
  }, [view]);

  // Задержку заводит эффект, то есть на рендер позже смены флага; до него смотрим на прошлое
  // состояние, иначе плитка на один рендер выпадает из списка и анимируется заново
  return (p) => p.playing || held.has(p.key) || prev.current.get(p.key) === true;
}

// Таб со счетчиком-бейджем после названия; у «Все» счетчика нет
function tab(value: View, text: string, count?: number): Tab<View> {
  return {
    value,
    label: (
      <>
        {text}
        {count !== undefined && <span className={styles.page__count}>{count}</span>}
      </>
    ),
  };
}

// Сетка плиток или текст, когда показывать нечего
// Пустая вкладка: текст на всю ширину, плитка состояния комнат остается
function Grid({ empty, children }: { empty?: string; children: ReactNode }) {
  return (
    <div className={styles.grid}>
      {empty && <p className={cx(styles.empty, styles.grid__empty)}>{empty}</p>}
      {children}
    </div>
  );
}

// Последняя плитка сетки, пока не все комнаты подключились: загрузчик и ошибки комнат
// с «Повторить». Место ровно под плитку, без фона и рамки
function RoomsStatus({
  rooms,
  onRetry,
}: {
  rooms: RefereeRoomInfo[];
  onRetry?: (room: RoomRef) => void;
}) {
  const isConnecting = rooms.some((r) => r.connection === 'connecting');
  const failed = rooms.filter((r) => r.connection === 'error');
  return (
    <div className={styles.status}>
      {isConnecting && <Loader preset="mobile" ignoreClubColors />}
      {failed.map((room) => (
        <p key={`${room.tournamentId}:${room.roomNumber}`} className={styles.status__error}>
          <span>
            Комната {room.roomNumber}
            {room.title ? ` турнира «${room.title}»` : ''} не подключилась
            {room.error ? `: ${room.error}` : ''}
          </span>
          {onRetry && <FancyBadge onClick={() => onRetry(room)}>Повторить</FancyBadge>}
        </p>
      ))}
    </div>
  );
}

function Notice({
  icon,
  title,
  text,
  children,
}: {
  icon?: ReactNode;
  title?: string;
  text?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.notice}>
      {icon && <div className={styles.notice__icon}>{icon}</div>}
      {title && <h2 className={styles.notice__title}>{title}</h2>}
      {text && <p className={styles.notice__text}>{text}</p>}
      {children}
    </div>
  );
}
